import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Input } from "@progress/kendo-react-inputs";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { updateCurrUser } from "../../../../../actions/auth";
import { inviteUsers, updateUserLoginType } from "../../../../../components/Admin/admin.service";
import { useGetUser } from "../../../../../components/UserProfileSetting/hooks/userProfileHelper.hooks";
import { icon, label, number } from "../../../../../config";
import { notifyIcon, quote } from "../../../../../config/constants";
import { resetPassword } from "../../../../../shared/services/auth.service";
import { loadState } from "../../../../../shared/services/session.service";
import { isValidMobileNumber } from "../../../../../shared/validators/validator";
import { getNotification, isTrueBit } from "../../../../../utils/common";
import ChangePassword from "../../ChangePassword/ChangePassword";

/**
 * LoginSettingsSection component for Login Options section
 * Displays login settings like Google SSO, User ID, SMS OTP
 * @author Bhavana
 */
const LoginSettingsSection = () => {
  const user = useGetUser();
  const dispatch = useDispatch();
  const history = useHistory();
  const { uId } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  const [isGoogleUser, setIsGoogleUser] = useState(false);

  const { IsGoogleSignUp, IsManualSignUp, companyId, MobileNo } = user;

  /**
   * Initializes input values when the component renders.
   * @author Bhavana
   */
  useEffect(() => {
    setIsGoogleUser(loadState(label.GOOGLE_USER));
    setMobileNumber(MobileNo ?? "");
  }, [user.id]);

  /**
   * change state bool true to open modal popup
   * @author Bhavana
   */
  const openModal = () => {
    setShowPassword(true);
  };

  /**
   * change state bool false to close modal popup
   * @author Bhavana
   */
  const hideModal = () => {
    setShowPassword(false);
  };

  /**
   * Handles the change of login type (activation or deactivation).
   * @param {string} field - The id of the dropdown field.
   * @param {string} eventVal - The selected value (ACTIVATE or DEACTIVATE).
   * @author Bhavana
   */
  const handleLoginTypeChange = async (field, eventVal) => {
    const isGoogleSSO = field === "userprofilesidebar-google-sso-dropdown";
    const activateLoginType = eventVal === label.ACTIVATE;

    if (eventVal === label.DEACTIVATE) {
      if (!canDeactivateLoginType(isGoogleSSO)) {
        getNotification(quote.LOGIN_TYPES_DISABLE_ERROR, notifyIcon.WARNING_ICON);
        return;
      }
    }

    const userId = user?.id;

    const loginEmailData = {
      userName: user?.name,
      userEmail: user?.email,
    };

    const { updatedLoginType } = await updateUserLoginType({
      userId,
      googleSSO: isGoogleSSO ? activateLoginType : null,
      activate: isGoogleSSO ? null : activateLoginType,
      adminId: userId,
      loginEmailData,
      isSMSOtp: null,
      mobileNo: null,
    });

    if (updatedLoginType) {
      await updateUserLoginState(isGoogleSSO, updatedLoginType);
      if (isGoogleSSO) {
        const message = activateLoginType ? "Google SSO Login Enabled" : "Google SSO Login Disabled";
        getNotification(message, notifyIcon.SUCCESS_ICON);
      } else {
        const message = activateLoginType ? "User ID Login Enabled" : "User ID Login Disabled";
        getNotification(message, notifyIcon.SUCCESS_ICON);
      }
    }
  };

  /**
   * Handles mobile number update when input loses focus
   * @param {Object} event - The event object
   * @author Bhavana
   */
  const handleMobileNumberBlur = async (event) => {
    if (!event.target.value) return;

    const mobileNumberValue = event.target.value.trim();
    if (!isValidMobileNumber(mobileNumberValue)) {
      getNotification(quote.INVALID_MOBILE_NUMBER, notifyIcon.ERROR_ICON);
      return;
    }

    await updateUserLoginType({
      userId: user?.id,
      googleSSO: null,
      activate: false,
      adminId: null,
      loginEmailData: null,
      isSMSOtp: null,
      mobileNo: mobileNumberValue,
    });

    dispatch(updateCurrUser({ MobileNo: mobileNumberValue }));
  };

  /**
   * Checks if the login type can be deactivated.
   * @param {boolean} googleSSO - Whether the check is for Google SSO.
   * @returns {boolean} Whether the login type can be deactivated.
   * @author Bhavana
   */
  const canDeactivateLoginType = (googleSSO) => {
    return (googleSSO && isTrueBit(user, "IsManualSignUp")) || (!googleSSO && isTrueBit(user, "IsGoogleSignUp"));
  };

  /**
   * Updates the user's login state in the store.
   * @author Bhavana
   */
  const updateUserLoginState = async (googleSSO, updatedLoginType) => {
    if (googleSSO) {
      await dispatch(updateCurrUser({ IsGoogleSignUp: updatedLoginType }));
    } else {
      await dispatch(updateCurrUser({ IsManualSignUp: updatedLoginType }));
    }
  };

  /**
   * Handles SMS OTP login permission toggle.
   * @author Bhavana
   */
  const handleLoginPermission = async (isEnabled) => {
    await updateUserLoginType({
      userId: user?.id,
      googleSSO: null,
      activate: null,
      adminId: null,
      loginEmailData: null,
      isSMSOtp: isEnabled,
      mobileNo: mobileNumber,
    });
    dispatch(updateCurrUser({ IsSMSOtp: isEnabled }));
    const message = isEnabled ? "OTP Login Enabled" : "OTP Login Disabled";
    getNotification(message, notifyIcon.SUCCESS_ICON);
  };

  return (
    <div className='profile-section-content mt-2 mb-2 bg-light'>
      <div className='bg-white'>
        {user?.IsSSOEnabled ? (
          <div className='form-group login-setting-container'>
            <div className='d-flex align-items-center justify-content-between mb-1'>
              <label className='mb-0'>{label.GOOGLE_SSO}</label>
              <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                <button
                  type='button'
                  id='userprofilesidebar-google-sso-dropdown'
                  className={`login-toggle-btn ${user?.IsGoogleSignUp?.data[0] ? "login-toggle-btn-disable" : "login-toggle-btn-disabled"}`}
                  title={user?.IsGoogleSignUp?.data[0] ? "Click to Disable" : "Click to Enable"}
                  onClick={async (e) => {
                    e.preventDefault();
                    await handleLoginTypeChange(
                      "userprofilesidebar-google-sso-dropdown",
                      user?.IsGoogleSignUp?.data[0] ? label.DEACTIVATE : label.ACTIVATE
                    );
                  }}>
                  {user?.IsGoogleSignUp?.data[0] ? label.DISABLE : label.DISABLED}
                </button>
              </Tooltip>
            </div>
            <Input type='text' value={user.email} disabled={uId} className='w-100 input-fixed-width' />
          </div>
        ) : null}

        {user?.IsUserIdEnabled ? (
          <div className='form-group login-setting-container'>
            <div className='d-flex align-items-center justify-content-between mb-1'>
              <label className='mb-0'>{label.USER_ID}</label>
              <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                <button
                  type='button'
                  id='userprofilesidebar-user-id-dropdown'
                  className={`login-toggle-btn ${user?.IsManualSignUp?.data[0] ? "login-toggle-btn-disable" : "login-toggle-btn-disabled"}`}
                  title={user?.IsManualSignUp?.data[0] ? "Click to Disable" : "Click to Enable"}
                  onClick={async (e) => {
                    e.preventDefault();
                    await handleLoginTypeChange("userprofilesidebar-id-dropdown", user?.IsManualSignUp?.data[0] ? label.DEACTIVATE : label.ACTIVATE);
                  }}>
                  {user?.IsManualSignUp?.data[0] ? label.DISABLE : label.DISABLED}
                </button>
              </Tooltip>
            </div>
            <Input type='text' value={user.email} disabled={uId} className='w-100 input-fixed-width' />
            {isTrueBit(user, "IsManualSignUp") && (
              <div className='d-flex'>
                {isGoogleUser && user?.IsUserIdEnabled && (
                  <span
                    className='small link-text cursor-pointer mt-2 mr-4'
                    id='userprofilesidebar-change-password-on-click'
                    onClick={(e) => {
                      e.preventDefault();
                      openModal();
                    }}>
                    {label.CHANGE_PASSWORD}
                  </span>
                )}
                <span
                  className='small link-text cursor-pointer mt-2'
                  onClick={() => dispatch(resetPassword({ userEmailId: user?.email, companyId: user?.companyId }, history))}>
                  {label.RESET_PASSWORD}
                </span>
              </div>
            )}
          </div>
        ) : null}

        {user?.IsSMSOtpEnabled ? (
          <div className='form-group mb-0 login-setting-container'>
            <div className='d-flex align-items-center justify-content-between'>
              <label>{label.SMS_OTP}</label>
              <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                <button
                  type='button'
                  id='userprofilesidebar-sms-otp-switch'
                  className={`login-toggle-btn ${user?.IsSMSOtp ? "login-toggle-btn-disable" : "login-toggle-btn-disabled"}`}
                  title={user?.IsSMSOtp ? "Click to Disable" : "Click to Enable"}
                  onClick={(e) => {
                    e.preventDefault();
                    handleLoginPermission(!user?.IsSMSOtp);
                  }}>
                  {user?.IsSMSOtp ? label.DISABLE : label.DISABLED}
                </button>
              </Tooltip>
            </div>
            <Input
              type='tel'
              placeholder='Mobile Number'
              maxLength={number.TEN}
              value={mobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9+\-\s()]/g, "");
                setMobileNumber(value);
              }}
              onBlur={handleMobileNumberBlur}
              className='mr-2 w-100 input-fixed-width'
              id='userprofilesidebar-sms-otp-mobile-number-input'
              disabled={!user?.IsSMSOtp}
            />
          </div>
        ) : null}
      </div>
      {showPassword && (
        <ChangePassword
          show={showPassword}
          hideModal={() => {
            hideModal();
          }}
        />
      )}
    </div>
  );
};

export default LoginSettingsSection;
